/** @format */

import { useGTMListType } from '@/ducks/general/hooks/useGTMListType';
import { GTMmenu } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TTranslation, useAppTranslation } from '@/i18n/declarations';
import { useAppDispatch } from '@/redux/hooks';
import calculateListingUrl from '@/utils/gatsby/calculate-listing-url';
import { utilityCitiesRedirect } from '@/utils/helpers/utilityCitiesRedirect';
import { useLocation } from '@reach/router';
import { Link } from 'gatsby-plugin-intl';
import React, { FunctionComponent as FC, useCallback, useMemo } from 'react';
import { CITIES, CityType } from '../../../@ducks/locations/constants';
import { CityItem } from './index.style';
import { utilityCreateIntlProvider } from '@/i18n/createProvider';

export const CitiesToRender: FC<{
  activeCountry: string;
  closeDropdown?: () => void;
}> = ({ activeCountry, closeDropdown }) => {
  const { intl, t } = useAppTranslation();
  const { intlEN } = utilityCreateIntlProvider();
  const { onChangeGTMListType } = useGTMListType();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const cityName = useCallback((city: CityType) => t(city.name as TTranslation), [t]);

  const sortedCities = useMemo(() => {
    const sortCities = (a: any, b: any) => {
      if (cityName(a) > cityName(b)) return 1;
      if (cityName(a) < cityName(b)) return -1;
      return 0;
    };
    return CITIES.sort(sortCities);
  }, [CITIES]);

  const handleClick = (option: CityType) => {
    const { country } = option;
    const countryName = intlEN.formatMessage({ id: `menu_living_${country}` });
    const formattedName = cityName(option).toLowerCase();
    GTMmenu(countryName.toLowerCase(), formattedName);
    onChangeGTMListType('search_result');
    if (location.href.includes(formattedName)) {
      closeDropdown?.();
      dispatch({
        type: 'LISTING_SET_MAP_AREA_FILTER',
        payload: undefined,
      });
      dispatch({
        type: 'LISTING_SET_SEARCH_BY_MAP',
        payload: false,
      });
    }
  };

  return (
    <>
      {sortedCities
        .filter(city => city.country.toLowerCase() === activeCountry)
        .map(city => {
          const { redirectCities, isRedirectCity } = utilityCitiesRedirect({
            city: cityName(city),
            locale: intl.locale,
            rawCityName: city.name,
          });
          const href = isRedirectCity ? redirectCities : `/${calculateListingUrl(city.name, intl, 'all')}`;

          return (
            <CityItem key={city.id}>
              {isRedirectCity && (
                <a href={href} onClick={() => handleClick(city)}>
                  <LinkContent cityName={cityName(city)} hot={city.hot} />
                </a>
              )}
              {!isRedirectCity && (
                <Link to={href} onClick={() => handleClick(city)}>
                  <LinkContent cityName={cityName(city)} hot={city.hot} />
                </Link>
              )}
            </CityItem>
          );
        })}
    </>
  );
};

const LinkContent: FC<{ cityName: string; hot: boolean }> = ({ cityName, hot }) => {
  const { t } = useAppTranslation();
  return (
    <>
      {cityName}
      {hot && (
        <>
          <span>-</span>
          <span className="hot">{t('popular')} 🔥</span>
        </>
      )}
    </>
  );
};
